(() => {
  const ELEMENT_NAME = 'dj-product-photo';
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  const MIME_BY_EXTENSION = new Map([
    ['jpg', 'image/jpeg'],
    ['jpeg', 'image/jpeg'],
    ['png', 'image/png'],
    ['webp', 'image/webp'],
  ]);
  const REQUIRED_MESSAGE = 'Lägg till ett foto innan du fortsätter.';
  const FORMAT_MESSAGE = 'Välj en bild i formatet JPG, JPEG, PNG eller WebP.';

  if (customElements.get(ELEMENT_NAME)) return;

  class DjProductPhoto extends HTMLElement {
    constructor() {
      super();
      this.inputs = [];
      this.form = null;
      this.objectUrls = new Map();
      this.inputHandlers = new Map();
      this.removeHandlers = new Map();
      this.disabledStates = new Map();
      this.submitStates = new Map();
      this.expectedPropertyKeys = [];
      this.uploading = false;
      this.connected = false;
      this.handleBusySubmit = this.handleBusySubmit.bind(this);
      this.handleInvalid = this.handleInvalid.bind(this);
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.form = this.closest('form');
      this.inputs = Array.from(this.querySelectorAll('[data-photo-input]'));
      this.setAttribute('aria-busy', this.uploading ? 'true' : 'false');

      this.inputs.forEach((input) => {
        const handler = () => this.handleInputChange(input);
        this.inputHandlers.set(input, handler);
        input.addEventListener('change', handler);

        const remove = input.closest('[data-photo-slot]')?.querySelector('[data-photo-remove]');
        if (remove) {
          const removeHandler = (event) => this.removePhoto(event, input);
          this.removeHandlers.set(remove, removeHandler);
          remove.addEventListener('click', removeHandler);
        }

        this.syncInputValidity(input);
        const file = input.files?.[0];
        if (file && this.validateFile(file).valid) this.showPreview(input, file);
      });

      this.addEventListener('invalid', this.handleInvalid, true);
      this.form?.addEventListener('submit', this.handleBusySubmit, true);
    }

    disconnectedCallback() {
      this.inputHandlers.forEach((handler, input) => input.removeEventListener('change', handler));
      this.removeHandlers.forEach((handler, button) => button.removeEventListener('click', handler));
      this.removeEventListener('invalid', this.handleInvalid, true);
      this.form?.removeEventListener('submit', this.handleBusySubmit, true);
      this.setUploading(false);
      this.revokeAllPreviews();
      this.inputHandlers.clear();
      this.removeHandlers.clear();
      this.inputs = [];
      this.form = null;
      this.connected = false;
    }

    handleInputChange(input) {
      this.expectedPropertyKeys = [];
      this.clearPreview(input);
      input.setCustomValidity('');

      const files = Array.from(input.files || []);
      if (!files.length) {
        this.syncInputValidity(input);
        this.clearError();
        this.announceSelection();
        return;
      }

      const result = files.length === 1
        ? this.validateFile(files[0])
        : { valid: false, message: 'Välj en bild per fält.' };

      if (!result.valid) {
        input.setCustomValidity(result.message);
        this.showRemoveButton(input, true);
        this.showError(result.message);
        this.reportInput(input);
        return;
      }

      this.showPreview(input, files[0]);
      this.clearError();
      this.announceSelection();
    }

    removePhoto(event, input) {
      event.preventDefault();
      input.value = '';
      this.expectedPropertyKeys = [];
      this.clearPreview(input);
      this.syncInputValidity(input);
      this.clearError();
      this.announceSelection();
      input.focus({ preventScroll: true });
    }

    validateFile(file) {
      if (!file || file.size === 0) {
        return { valid: false, message: 'Filen är tom. Välj en annan bild.' };
      }
      if (file.size > MAX_FILE_SIZE) {
        return { valid: false, message: 'Bilden får vara högst 10 MB.' };
      }

      const extension = String(file.name || '').split('.').pop().toLowerCase();
      const mimeType = String(file.type || '').toLowerCase();
      if (!MIME_BY_EXTENSION.has(extension) || MIME_BY_EXTENSION.get(extension) !== mimeType) {
        return { valid: false, message: FORMAT_MESSAGE };
      }
      return { valid: true, message: '' };
    }

    validate() {
      if (!this.inputs.length || !this.form) {
        this.showError('Bilduppladdningen kunde inte startas. Ladda om sidan och försök igen.');
        return false;
      }

      let firstInvalid = null;
      let firstMessage = '';

      this.inputs.forEach((input) => {
        input.setCustomValidity('');
        const files = Array.from(input.files || []);
        let result = { valid: true, message: '' };

        if (!files.length && input.required) {
          result = { valid: false, message: REQUIRED_MESSAGE };
        } else if (files.length > 1) {
          result = { valid: false, message: 'Välj en bild per fält.' };
        } else if (files.length === 1) {
          result = this.validateFile(files[0]);
        }

        if (!result.valid) {
          input.setCustomValidity(result.message);
          if (!firstInvalid) {
            firstInvalid = input;
            firstMessage = result.message;
          }
        }
      });

      if (firstInvalid) {
        this.showError(firstMessage);
        this.reportInput(firstInvalid);
        return false;
      }

      this.clearError();
      return true;
    }

    setUploading(uploading) {
      const nextState = Boolean(uploading);
      this.uploading = nextState;
      this.setAttribute('aria-busy', nextState ? 'true' : 'false');

      if (nextState) {
        this.expectedPropertyKeys = this.selectedPropertyKeys();
        this.lockLocalControls();
        this.markSubmitControlsBusy();
      } else {
        this.restoreLocalControls();
        this.restoreSubmitControls();
      }
    }

    markAdded(response) {
      const expectedKeys = this.expectedPropertyKeys.length
        ? this.expectedPropertyKeys
        : this.selectedPropertyKeys();
      const properties = response && typeof response.properties === 'object' ? response.properties : null;
      const confirmed = expectedKeys.length > 0 && expectedKeys.every((key) => (
        properties && Object.prototype.hasOwnProperty.call(properties, key) && this.isShopifyUploadUrl(properties[key])
      ));

      if (!confirmed) {
        this.showError('Bilduppladdningen kunde inte bekräftas. Kontrollera varukorgen innan du försöker igen.');
        return false;
      }

      this.clearError();
      const status = this.querySelector('[data-photo-status]');
      if (status) {
        status.textContent = expectedKeys.length === 1
          ? 'Bilden har lagts till med produkten.'
          : `${expectedKeys.length} bilder har lagts till med produkten.`;
      }
      return true;
    }

    showError(message) {
      const error = this.querySelector('[data-photo-error]');
      const status = this.querySelector('[data-photo-status]');
      if (status) status.textContent = '';
      if (!error) return;
      error.textContent = String(message || '');
      error.hidden = !error.textContent;
    }

    clearError() {
      this.showError('');
    }

    syncInputValidity(input) {
      input.setCustomValidity(input.required && !input.files?.length ? REQUIRED_MESSAGE : '');
    }

    reportInput(input) {
      const details = input.closest('details');
      if (details) details.open = true;
      input.reportValidity();
      if (document.activeElement !== input) input.focus({ preventScroll: true });
    }

    handleInvalid(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-photo-input]')) return;
      const details = input.closest('details');
      if (details) details.open = true;
    }

    handleBusySubmit(event) {
      if (!this.uploading) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    showPreview(input, file) {
      const slot = input.closest('[data-photo-slot]');
      const preview = slot?.querySelector('[data-photo-preview]');
      if (preview && URL.createObjectURL) {
        const objectUrl = URL.createObjectURL(file);
        this.objectUrls.set(input, objectUrl);
        preview.src = objectUrl;
        preview.hidden = false;
      }
      this.showRemoveButton(input, true);
    }

    clearPreview(input) {
      const objectUrl = this.objectUrls.get(input);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      this.objectUrls.delete(input);

      const slot = input.closest('[data-photo-slot]');
      const preview = slot?.querySelector('[data-photo-preview]');
      if (preview) {
        preview.removeAttribute('src');
        preview.hidden = true;
      }
      this.showRemoveButton(input, false);
    }

    showRemoveButton(input, visible) {
      const remove = input.closest('[data-photo-slot]')?.querySelector('[data-photo-remove]');
      if (remove) remove.hidden = !visible;
    }

    revokeAllPreviews() {
      this.objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
      this.objectUrls.clear();
    }

    announceSelection() {
      const count = this.inputs.filter((input) => input.files?.length).length;
      const status = this.querySelector('[data-photo-status]');
      if (!status) return;
      status.textContent = count === 0 ? '' : count === 1 ? '1 bild vald.' : `${count} bilder valda.`;
    }

    selectedPropertyKeys() {
      return this.inputs
        .filter((input) => input.files?.length)
        .map((input) => /^properties\[(Bild [1-3])\]$/.exec(input.name || '')?.[1])
        .filter(Boolean);
    }

    isShopifyUploadUrl(value) {
      if (typeof value !== 'string') return false;
      try {
        const url = new URL(value);
        return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && url.pathname.includes('/uploads/');
      } catch {
        return false;
      }
    }

    localControls() {
      return [...this.inputs, ...this.querySelectorAll('[data-photo-remove]')];
    }

    lockLocalControls() {
      this.localControls().forEach((control) => {
        if (!this.disabledStates.has(control)) this.disabledStates.set(control, Boolean(control.disabled));
        control.disabled = true;
      });
    }

    restoreLocalControls() {
      this.disabledStates.forEach((wasDisabled, control) => {
        control.disabled = wasDisabled;
      });
      this.disabledStates.clear();
    }

    submitControls() {
      if (!this.form) return [];
      return Array.from(this.form.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type])'));
    }

    markSubmitControlsBusy() {
      this.submitControls().forEach((control) => {
        if (!this.submitStates.has(control)) {
          this.submitStates.set(control, {
            ariaDisabled: control.getAttribute('aria-disabled'),
            ariaBusy: control.getAttribute('aria-busy'),
          });
        }
        control.setAttribute('aria-disabled', 'true');
        control.setAttribute('aria-busy', 'true');
      });
    }

    restoreSubmitControls() {
      this.submitStates.forEach((state, control) => {
        this.restoreAttribute(control, 'aria-disabled', state.ariaDisabled);
        this.restoreAttribute(control, 'aria-busy', state.ariaBusy);
      });
      this.submitStates.clear();
    }

    restoreAttribute(element, name, value) {
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    }
  }

  customElements.define(ELEMENT_NAME, DjProductPhoto);
})();
